<script>
  import { createEventDispatcher, onMount, onDestroy } from "svelte";
  import { derived, writable } from "svelte/store";
  import { itemStore } from "./itemStore.js";

  export let itemId; // ID of the item being edited
  export let deleteItem;

  const dispatch = createEventDispatcher();

  let item = writable(null);
  let newVariableValue = writable("");
  let selectedVariableName = writable(""); // Intermediary writable store

  // Derived store for selectedVariable
  const selectedVariable = derived(
    [item, selectedVariableName],
    ([$item, $selectedVariableName]) => {
      if ($item && $item.variables) {
        const activeVariable =
          $item.variables.find((v) => v.active) ||
          $item.variables.find((v) => v.name === $selectedVariableName) ||
          $item.variables[0] ||
          {};
        return activeVariable.name || "";
      }
      return "";
    },
  );

  // Derived store for selectedVariableValue
  const selectedVariableValue = derived(
    [item, selectedVariable],
    ([$item, $selectedVariable]) => {
      if ($item && $item.variables) {
        const variable = $item.variables.find(
          (v) => v.name === $selectedVariable,
        );
        if (variable && variable.values) {
          const activeValue = variable.values.find((v) => v.active) ||
            variable.values[0] || { value: "" };
          return activeValue.value;
        }
      }
      return "";
    },
  );

  // Derived store for variableValues
  const variableValues = derived(selectedVariable, ($selectedVariable) => {
    const variable = $item?.variables?.find(
      (v) => v.name === $selectedVariable,
    );
    return variable ? variable.values.map((v) => v.value) : [];
  });

  // Function to load item data
  function loadItemData() {
    const items = $itemStore;
    const currentItem = items.find((i) => i.id === itemId);
    if (currentItem) {
      item.set(currentItem);
      newVariableValue.set("");
      selectedVariableName.set(
        currentItem.variables.find((v) => v.active)?.name ||
          currentItem.variables[0]?.name ||
          "",
      );
    }
  }

  let unsubscribe;

  onMount(() => {
    unsubscribe = itemStore.subscribe(() => {
      loadItemData();
    });
  });

  onDestroy(() => {
    if (unsubscribe) {
      unsubscribe();
    }
  });

  // Watch for changes to itemId and reload item data
  $: if (itemId !== undefined) {
    loadItemData();
  }

  function onVariableValueSubmit(event) {
    event.preventDefault();
    if ($newVariableValue.trim()) {
      itemStore.addVariableValue(
        itemId,
        $selectedVariable,
        $newVariableValue.trim(),
      );
      newVariableValue.set("");
    }
  }

  function onVariableChange(event) {
    const selectedVar = event.target.value;
    selectedVariableName.set(selectedVar); // Update the intermediary store
    itemStore.updateVariableActiveState(itemId, selectedVar);
  }

  function onVariableValueChange(event) {
    const selectedValue = event.target.value;
    itemStore.updateVariableValueActiveState(
      itemId,
      $selectedVariable,
      selectedValue,
    );
  }

  function onDeleteValue() {
    itemStore.deleteVariableValue(
      itemId,
      $selectedVariable,
      $selectedVariableValue,
    );
  }

  function onDeleteItem() {
    deleteItem(itemId);
  }
</script>

<div class="edit-container">
  <div class="input-group">
    <label for="command">Template:</label>
    <input id="command" type="text" bind:value={$item.text} readonly />
  </div>

  {#if $item?.variables?.length > 0}
    <div class="input-group">
      <label for="variable">Variables:</label>
      <select
        id="variable"
        bind:value={$selectedVariableName}
        on:change={onVariableChange}
      >
        {#each $item.variables as variable}
          <option value={variable.name}>{variable.name}</option>
        {/each}
      </select>
    </div>
    <div class="input-group">
      <label for="variableValue">Values:</label>
      <select
        id="variableValue"
        bind:value={$selectedVariableValue}
        on:change={onVariableValueChange}
      >
        {#each $variableValues as value}
          <option {value}>{value}</option>
        {/each}
      </select>
    </div>
    <form class="input-group new-value-group" on:submit={onVariableValueSubmit}>
      <label for="newVariableValue"
        >Add New Value for {$selectedVariable}:</label
      >
      <div class="new-value-input">
        <input
          id="newVariableValue"
          type="text"
          bind:value={$newVariableValue}
          placeholder="Type new value and press Enter"
        />
        <button type="submit">Add</button>
      </div>
      {#if $selectedVariableValue}
        <div class="delete-button-wrapper">
          <button type="button" on:click={onDeleteValue}>
            Delete value '{$selectedVariableValue}'
          </button>
        </div>
      {/if}
    </form>
  {/if}

  <div class="input-group">
    <label for="finalCommand">Command:</label>
    <input
      id="finalCommand"
      type="text"
      bind:value={$item.finalCommand}
      readonly
    />
  </div>

  <div class="delete-item-button-wrapper">
    <button class="delete-item-button" type="button" on:click={onDeleteItem}
      >Delete Item</button
    >
  </div>
</div>

<style>
  .edit-container {
    max-width: 400px;
    margin: 20px auto;
    padding: 20px;
    background-color: #f9f9f9;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  .input-group {
    display: flex;
    flex-direction: column;
    margin-bottom: 20px;
  }
  .new-value-group {
    flex-direction: column;
  }
  .new-value-input {
    display: flex;
    margin-top: 5px;
  }
  label {
    margin-bottom: 5px;
    font-weight: bold;
  }
  input,
  select {
    padding: 10px;
    font-size: 16px;
    border: 1px solid #ccc;
    border-radius: 4px;
    width: 100%; /* Ensure the input and select elements are full width */
    box-sizing: border-box; /* Include padding and border in the element's total width and height */
  }
  button {
    padding: 10px 20px;
    font-size: 16px;
    background-color: #4caf50;
    color: white;
    border: 1px solid #4caf50;
    border-radius: 4px;
    cursor: pointer;
    margin-left: 10px;
  }
  button[type="button"] {
    margin-left: 0; /* Align button to the left */
    margin-top: 10px;
    background-color: #ff4c4c;
    border: 1px solid #ff4c4c;
  }
  .delete-button-wrapper {
    display: flex;
    justify-content: flex-start; /* Align button to the left */
  }
  .delete-item-button-wrapper {
    display: flex;
    justify-content: center;
    margin-top: 20px;
  }
  .delete-item-button {
    width: 100%;
    background-color: #ff4c4c;
    border: 1px solid #ff4c4c;
  }
  button:hover {
    background-color: #45a049;
  }
  button[type="button"]:hover {
    background-color: #ff3333;
  }
</style>
